import { useState, useEffect, useTransition, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search, X, Loader2, ArrowLeft, LayoutGrid,
  Apple, Milk, Beef, Egg, Wheat, Carrot, Fish, Cookie,
  Cherry, Grape, Banana, Citrus, Salad, Sandwich, Pizza, Package,
  Ham, Drumstick, IceCream, CakeSlice, Shell, Croissant,
  Candy, Cake, Coffee, Wine, Beer, GlassWater, Soup, UtensilsCrossed,
  ShoppingBasket, Store, Leaf
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '@/components/Header';
import ProductCardSkeleton from '@/components/ProductCardSkeleton';
import VirtualizedProductGrid from '@/components/VirtualizedProductGrid';
import ProductFilters, { ProductFiltersState, defaultFilters, SortOption } from '@/components/ProductFilters';
import PullToRefresh from '@/components/PullToRefresh';
import PageTransition from '@/components/PageTransition';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Product, isDiscountActive, getDiscountedPrice } from '@/types/product';
import { CategoryDB } from '@/types/admin';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

// Cached order stats for sorting by popularity
let orderStatsCache: Record<string, number> = {};

const categoryIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutGrid,
  // Овощи и фрукты
  Carrot, Apple, Cherry, Grape, Banana, Citrus, Salad, Leaf,
  // Мясо и деликатесы
  Beef, Ham, Drumstick, Sausage: Ham,
  // Молочные и яйца
  Milk, Egg, IceCream, CakeSlice,
  // Морепродукты
  Fish, Shell,
  // Выпечка и кондитерка
  Wheat, Cookie, Croissant, Candy, Cake,
  // Напитки
  Coffee, Wine, Beer, Cup: GlassWater,
  // Готовая еда
  Sandwich, Pizza, Soup, UtensilsCrossed,
  // Прочее
  Package, ShoppingBasket, Store,
};

const CategoryIcon = ({ name, className }: { name?: string; className?: string }) => {
  const Icon = categoryIconMap[name || ''] || Package;
  return <Icon className={className} />;
};

const Catalog = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryDB[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ProductFiltersState>(defaultFilters);
  const [isPending, startTransition] = useTransition();
  const [isFiltering, setIsFiltering] = useState(false);
  const categoryFromUrl = searchParams.get('category');
  const [activeCategory, setActiveCategory] = useState(
    !categoryFromUrl || categoryFromUrl === 'Все' || categoryFromUrl === 'all' ? 'all' : categoryFromUrl
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [productsRes, categoriesRes, orderStatsRes] = await Promise.all([
        supabase.from('products').select('*').order('created_at', { ascending: false }),
        supabase.from('categories').select('*').order('sort_order'),
        supabase.from('product_order_stats').select('product_id, order_count'),
      ]);

      if (productsRes.error) throw productsRes.error;
      if (categoriesRes.error) throw categoriesRes.error;

      setProducts(productsRes.data || []);
      setCategories(categoriesRes.data || []);

      // Cache order stats
      orderStatsCache = {};
      (orderStatsRes.data || []).forEach((item: { product_id: string; order_count: number }) => {
        orderStatsCache[item.product_id] = item.order_count;
      });
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Не удалось загрузить товары. Проверьте подключение к базе данных.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Find active category name for filtering products
  const activeCategoryName = activeCategory === 'all'
    ? null
    : categories.find(c => c.slug === activeCategory)?.name || activeCategory;

  // Sort function
  const sortProducts = (products: Product[], sortBy: SortOption): Product[] => {
    const sorted = [...products];
    switch (sortBy) {
      case 'price_asc':
        return sorted.sort((a, b) => {
          const priceA = isDiscountActive(a) ? getDiscountedPrice(a) : a.price;
          const priceB = isDiscountActive(b) ? getDiscountedPrice(b) : b.price;
          return priceA - priceB;
        });
      case 'price_desc':
        return sorted.sort((a, b) => {
          const priceA = isDiscountActive(a) ? getDiscountedPrice(a) : a.price;
          const priceB = isDiscountActive(b) ? getDiscountedPrice(b) : b.price;
          return priceB - priceA;
        });
      case 'date_desc':
        return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case 'date_asc':
        return sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case 'popular':
        return sorted.sort((a, b) => (orderStatsCache[b.id] || 0) - (orderStatsCache[a.id] || 0));
      default:
        return sorted;
    }
  };

  const filteredProducts = useMemo(() => {
    const filtered = products.filter((product) => {
      const matchesCategory = activeCategory === 'all' || product.category === activeCategoryName;
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

      // Advanced filters
      const matchesNew = !filters.showNewOnly || product.is_new;
      const matchesBestseller = !filters.showBestsellersOnly || product.is_bestseller;
      const matchesDiscount = !filters.showDiscountOnly || isDiscountActive(product);
      const matchesInStock = !filters.showInStockOnly || product.in_stock;

      // Price filter
      const productPrice = isDiscountActive(product) ? getDiscountedPrice(product) : product.price;
      const priceMin = filters.priceMin ? parseFloat(filters.priceMin) : 0;
      const priceMax = filters.priceMax ? parseFloat(filters.priceMax) : Infinity;
      const matchesPrice = productPrice >= priceMin && productPrice <= priceMax;

      return matchesCategory && matchesSearch && matchesNew && matchesBestseller &&
             matchesDiscount && matchesInStock && matchesPrice;
    });

    return sortProducts(filtered, filters.sortBy);
  }, [products, activeCategory, activeCategoryName, searchQuery, filters]);

  const handleCategoryChange = (slug: string) => {
    setIsFiltering(true);
    startTransition(() => {
      setActiveCategory(slug);
      if (slug === 'all') {
        searchParams.delete('category');
      } else {
        searchParams.set('category', slug);
      }
      setSearchParams(searchParams);
      setTimeout(() => setIsFiltering(false), 150);
    });
  };

  const allCategories = useMemo(
    () => [{ id: 'all', name: 'Все', slug: 'all', icon: 'LayoutGrid', is_eco: false, sort_order: 0, created_at: '' }, ...categories],
    [categories]
  );

  return (
    <PageTransition>
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="min-h-screen flex flex-col bg-background pb-24 md:pb-0">
          <Header />
          <main className="flex-1">
            <div className="max-w-6xl mx-auto px-3 md:px-6 pt-4 md:pt-8">
              {/* Mobile top bar */}
              <div className="flex items-center gap-3 mb-4 md:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 rounded-full hover:bg-secondary"
                  onClick={() => navigate(-1)}
                  aria-label="Назад"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <span className="font-semibold text-lg text-foreground">Каталог</span>
              </div>

              {/* Hero header */}
              <section className="relative overflow-hidden rounded-3xl md:rounded-[2.5rem] bg-secondary/70 border border-border/40">
                {/* Decorative blobs */}
                <div className="absolute -top-24 -right-24 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-20 -left-16 w-64 h-64 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />

                <div className="relative z-10 px-5 md:px-10 py-7 md:py-12">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
                    {/* Title block */}
                    <div className="space-y-3 md:space-y-4 max-w-2xl">
                      <span className="inline-flex items-center gap-1.5 badge-category">
                        <ShoppingBasket className="w-3 h-3" />
                        Каталог продукции
                      </span>
                      <h1 className="font-bold text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                        Свежее с местных{' '}
                        <span className="text-primary relative inline-block">
                          ферм
                          <span className="absolute -bottom-1 left-0 right-0 h-1.5 bg-primary/20 rounded-full" />
                        </span>
                      </h1>
                      {!loading && !error && (
                        <p className="text-sm md:text-base text-foreground/65">
                          Найдено:{' '}
                          <span className="font-semibold text-foreground">{filteredProducts.length}</span>{' '}
                          {filteredProducts.length === 1
                            ? 'товар'
                            : [2, 3, 4].includes(filteredProducts.length % 10) && filteredProducts.length % 100 !== 12 && filteredProducts.length % 100 !== 13 && filteredProducts.length % 100 !== 14
                              ? 'товара'
                              : 'товаров'}
                        </p>
                      )}
                    </div>

                    {/* Search */}
                    <div className="relative w-full md:max-w-sm lg:max-w-md">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
                      <Input
                        placeholder="Найти продукты..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-11 md:pl-12 pr-10 h-12 md:h-14 rounded-full bg-card/80 border-border/60 focus-visible:ring-primary/40 shadow-sm text-sm md:text-base"
                      />
                      {searchQuery && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 md:h-9 md:w-9 rounded-full hover:bg-secondary"
                          onClick={() => setSearchQuery('')}
                          aria-label="Очистить"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Filters + category pills */}
              <div className="mt-5 md:mt-7 flex flex-col gap-4">
                <ProductFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  variant="catalog"
                />

                <div className="relative -mx-3 md:mx-0">
                  <div className="flex gap-2 md:gap-2.5 overflow-x-auto px-3 md:flex-wrap md:overflow-visible md:px-0 pb-1 scrollbar-hide scroll-smooth">
                    {allCategories.map((category, index) => {
                      const isActive = activeCategory === category.slug;
                      const isAll = category.slug === 'all';
                      return (
                        <motion.button
                          key={category.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.02 }}
                          onClick={() => handleCategoryChange(category.slug)}
                          className={cn(
                            "shrink-0 inline-flex items-center gap-2 rounded-full text-sm h-10 md:h-11 px-4 md:px-5 font-medium transition-all border whitespace-nowrap",
                            isActive
                              ? "bg-foreground text-background border-foreground shadow-sm"
                              : "bg-card text-foreground/80 border-border/50 hover:border-primary/40 hover:text-foreground"
                          )}
                        >
                          <CategoryIcon
                            name={isAll ? 'LayoutGrid' : category.icon}
                            className={cn(
                              "w-4 h-4 md:w-5 md:h-5",
                              isActive ? "text-background" : "text-muted-foreground"
                            )}
                          />
                          {category.name}
                        </motion.button>
                      );
                    })}
                    <div className="shrink-0 w-4 md:hidden" aria-hidden />
                  </div>
                  <div className="md:hidden pointer-events-none absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-background to-transparent" />
                </div>
              </div>

              {/* Loading */}
              {loading && (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground text-sm">Загрузка товаров...</span>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="text-center py-16">
                  <p className="text-destructive mb-4 text-sm md:text-base">{error}</p>
                  <Button onClick={fetchData} className="rounded-full">Попробовать снова</Button>
                </div>
              )}

              {/* Results */}
              {!loading && !error && (
                <div className="mt-6 md:mt-8">
                  <AnimatePresence mode="wait">
                    {(isFiltering || isPending) ? (
                      <motion.div
                        key="skeleton"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4"
                      >
                        {Array.from({ length: 8 }).map((_, i) => (
                          <ProductCardSkeleton key={i} />
                        ))}
                      </motion.div>
                    ) : filteredProducts.length > 0 ? (
                      <motion.div
                        key="products"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <VirtualizedProductGrid
                          products={filteredProducts}
                          filterKey={`${activeCategory}-${searchQuery}-${JSON.stringify(filters)}`}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="empty"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="rounded-3xl bg-secondary/60 p-10 md:p-16 text-center"
                      >
                        <p className="text-foreground/60 text-sm md:text-base">
                          Ничего не найдено. Попробуйте изменить параметры поиска.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </main>
        </div>
      </PullToRefresh>
    </PageTransition>
  );
};

export default Catalog;
